import React, { useState, useRef, useCallback } from 'react';
import styles from './Accordion.module.css';
import { MdKeyboardArrowDown } from 'react-icons/md';

export interface AccordionItemProps {
  id: number | string;
  title: React.ReactNode;
  content: React.ReactNode;
  content2?: React.ReactNode;
  content3?: React.ReactNode;
  content4?: React.ReactNode;
}

interface AccordionProps {
  items: AccordionItemProps[];
  className?: string;
  allowMultiple?: boolean;
}

const Accordion: React.FC<AccordionProps> = ({ 
  items, 
  className, 
  allowMultiple = false 
}) => {
  const [expandedItems, setExpandedItems] = useState<(number | string)[]>([]);
  const contentRefs = useRef<Map<number | string, HTMLDivElement>>(new Map());
  
  const registerContentRef = useCallback((id: number | string, element: HTMLDivElement | null) => {
    if (element) {
      contentRefs.current.set(id, element);
    } else {
      contentRefs.current.delete(id);
    }
  }, []);
  
  const toggleItem = (id: number | string) => {
    setExpandedItems(prevState => {
      if (prevState.includes(id)) {
        return prevState.filter(item => item !== id);
      } else {
        if (allowMultiple) {
          return [...prevState, id];
        } else {
          return [id];
        }
      }
    });
  };

  const isExpanded = (id: number | string) => expandedItems.includes(id);

  return (
    <div className={`${styles.accordion} ${className || ''}`}>
      {items.map((item, index) => (
        <div 
          key={item.id} 
          className={`${styles.accordionItem} ${isExpanded(item.id) ? styles.expanded : ''}`}
        >
          <button 
            className={`${styles.accordionHeader} ${isExpanded(item.id) ? styles.expanded : ''}`} 
            onClick={() => toggleItem(item.id)}
            aria-expanded={isExpanded(item.id)}
          >
            <div className={styles.accordionTitle}>
              <p className={styles.accordionNumber}>{index + 1}.</p>
              <p>{item.title}</p>
            </div>
            <MdKeyboardArrowDown 
              className={`${styles.accordionIcon} ${isExpanded(item.id) ? styles.rotated : ''}`} 
              size={24} 
            />
          </button>
          <div 
            className={`${styles.accordionContent} ${isExpanded(item.id) ? styles.visible : ''}`}
            style={{
              // When expanded, don't constrain the max-height 
              maxHeight: isExpanded(item.id) ? undefined : 0
            }}
          >
            <div 
              ref={(el) => registerContentRef(item.id, el)} 
              className={styles.accordionContentInner}
            >
              <p>{item.content}</p>
              {item.content2 && <p>{item.content2}</p>}
              {item.content3 && <p>{item.content3}</p>}
              {item.content4 && <p>{item.content4}</p>}
            </div>
          </div>
          {index < items.length - 1 && <div className={styles.separator}></div>}
        </div>
      ))}
    </div>
  );
};

export default Accordion; 